import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { View, Pressable, useWindowDimensions, StyleSheet, Platform, Alert } from 'react-native';
import { Text, Title } from '@constants/Themed';
import { useTranslation } from 'react-i18next';
import { FontAwesome, Entypo, SimpleLineIcons, MaterialIcons, FontAwesome5 } from '@expo/vector-icons';
import DefaultScreen from '@components/Global/DefaultScreenView';
import DataCounter from '../components/DataCounter';
import apis from '@api/api';
import { useDispatch, useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import { useQuery } from '@tanstack/react-query';
import LottieView from '@utils/lottieAnimation';
import { filter, intersection } from 'lodash';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withSpring,
  runOnJS,
  interpolate,
} from 'react-native-reanimated';
import Svg, { Line, Defs, Pattern, Rect, Path, Circle, Text as SvgText } from 'react-native-svg';
import { XStack, YStack, Button } from 'tamagui';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import SwipeableRobot, { RobotHandle } from '@components/RobotHelper/SwipeableRobot';
import useGlobalUser from '@hooks/getGlobalUser';
import DataOptionsModal, { DataOptionsModalHandle } from '@components/modals/DataOptionsModal/DataOptionsModal';
import ActionsModal, { ActionModalHandle } from '@components/modals/ActionsModal/ActionsModal';
import DataDeleteConfirmModal, { DeleteConfirmModalHandle } from '@components/modals/DeleteConfirmModal/DataDeleteConfirmModal';
import ShareUserModal, { ShareConfirmHandle } from '@components/modals/ShareUserModal/ShareUserModal';
import QuickAddDataModal, { QuickAddHandle } from '@components/modals/QuickAddDataModal/QuickAddDataModal';
import { ReactNativeZoomableView, ZoomableViewEvent } from '@openspacelabs/react-native-zoomable-view';
import BubbleComponent, { BubbleData, BubbleComponentHandle } from './components/BubbleComponent';
import SankeyDiagram from './components/SankeyDiagram';
import { AntDesign } from '@expo/vector-icons';
import { Loading } from '@components/Global/Loading';

interface Connection {
  from: string;
  to: string;
  commonFields: string[];
  strength: number;
}

const DataBubbleScreen = ({ navigation }) => {
  const { t } = useTranslation();
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const [searchState, setSearchState] = useState('');
  const [activeItem, setActiveItem] = useState<any>({});
  const dataFilter = useSelector((state: DefaultRootState) => state.UI.dataFilter);
  const selectedId = useGlobalUser();
  const theme = themeSelector();

  // Refs for modals
  const dataOptionsRef = useRef<DataOptionsModalHandle>(null);
  const actionsRef = useRef<ActionModalHandle>(null);
  const deleteConfirmRef = useRef<DeleteConfirmModalHandle>(null);
  const shareUserRef = useRef<ShareConfirmHandle>(null);
  const quickAddRef = useRef<QuickAddHandle>(null);
  const robotRef = useRef<RobotHandle>(null);

  // Zoomable view ref
  const zoomRef = useRef<ReactNativeZoomableView>(null);
  const containerRef = useRef(null);

  // Zoom state for connection visibility
  const [zoomLevel, setZoomLevel] = useState(1);

  // View toggle state
  const [viewMode, setViewMode] = useState<'bubble' | 'sankey'>('bubble');

  // View dimensions - Reduced size for Android to prevent crashes
  const VIEW_HEIGHT = useMemo(() => Platform.OS === 'android' ? 1500 : 2000, []);
  const VIEW_WIDTH = useMemo(() => Platform.OS === 'android' ? 1500 : 2000, []);

  // Bubble positions state
  const [bubbles, setBubbles] = useState<BubbleData[]>([]);
  const [connections, setConnections] = useState<Connection[]>([]);

  // Refs for individual bubble components
  const bubbleRefs = useRef<{ [key: string]: BubbleComponentHandle }>({});

  // Handle bubble position changes
  const handleBubblePositionChange = useCallback((bubbleId: string, x: number, y: number) => {
    setBubbles(prevBubbles => {
      const updatedBubbles = prevBubbles.map(bubble => 
        bubble._id === bubbleId 
          ? { ...bubble, x, y }
          : bubble
      );
      
      return updatedBubbles;
    });
  }, []);

  // Get current zoom props for bubbles
  const [currentZoomProps, setCurrentZoomProps] = useState<ZoomableViewEvent>({
    zoomLevel: 1,
    offsetX: 0,
    offsetY: 0,
    originalHeight: windowHeight,
    originalWidth: windowWidth,
    originalPageX: 0,
    originalPageY: 0,
  });

  // Handle zoom updates for connection visibility
  const handleZoomUpdate = useCallback((event, gestureState, zoomableViewEventObject) => {
    setZoomLevel(zoomableViewEventObject.zoomLevel);
    setCurrentZoomProps(zoomableViewEventObject);
    return false;
  }, []);

  const textHelp = useMemo(() => {
    if (viewMode === 'bubble') {
      return [
        'Each bubble represents a data folder - larger bubbles have more data items',
        'You can zoom in and out by pinching, and pan around by dragging the background',
        'Tap on any bubble to interact with your data folder',
        'Drag bubbles around to organize them visually - they will stay in their new positions',
        'The color of bubbles indicates different data types or categories',
        'Connection lines between bubbles show shared data relationships and adapt to zoom level',
        'Use the tree icon in the toolbar to switch to Sankey diagram view',
      ];
    } else {
      return [
        'Sankey diagram shows data flow based on importance and relationships',
        'Data folders are organized into importance tiers dynamically based on your data',
        'You can zoom in and out by pinching, and pan around by dragging',
        'Importance is calculated from data count and number of connections',
        'Flow thickness represents the strength of relationships between data folders',
        'Titles are automatically formatted for better readability',
        'Tap on nodes to interact with data folders',
        'Use the bubble chart icon in the toolbar to switch back to bubble view',
        'This view helps identify the most critical data relationships in your system',
      ];
    }
  }, [viewMode]);

  // Fetch data folders
  const { data: dataFolderList, error, isFetching, isLoading, refetch } = useQuery({
    queryKey: ['dataFolders', dataFilter, selectedId],
    queryFn: async () => {
      const data = await apis.getDataFolders({ userId: selectedId, dataFilter: dataFilter || "all" });
      return data || [];
    },
    retry: 2,
    staleTime: 1000 * 60 * 5,
  });

  // Process data and filter
  const processedData = useMemo(() => {
    if (!dataFolderList?.length) return [];
    if (!searchState) return dataFolderList;

    const formattedQuery = searchState.toLowerCase().replace(/ /g, '_');
    return filter(dataFolderList, (item) =>
      item.dataTitle?.toLowerCase().includes(formattedQuery) || item.dataTags?.includes(formattedQuery)
    );
  }, [searchState, dataFolderList]);

  // Generate bubble colors
  const getBubbleColor = (index: number, count: number) => {
    const colors = [
      '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7',
      '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9'
    ];

    // Color intensity based on data count
    const intensity = Math.min(count / 100, 1); // Normalize to 0-1
    const baseColor = colors[index % colors.length];

    return baseColor;
  };

  // Generate bubble positions and sizes
  const generateBubbles = useCallback(() => {
    if (!processedData.length) return [];

    const maxCount = Math.max(...processedData.map(item => item.count || 0));
    const minRadius = 30;
    const maxRadius = 80;

    return processedData.map((item, index) => {
      const count = item.count || 0;
      const radius = minRadius + (count / maxCount) * (maxRadius - minRadius);

      // Generate random but distributed positions
      const angle = (index / processedData.length) * 2 * Math.PI;
      const distance = 100 + Math.random() * 150;
      const centerX = windowWidth / 2;
      const centerY = windowHeight / 2;

      return {
        _id: item._id,
        dataTitle: item.dataTitle || 'Untitled',
        count,
        dataTags: item.dataTags,
        x: centerX + Math.cos(angle) * distance,
        y: centerY + Math.sin(angle) * distance,
        radius,
        color: getBubbleColor(index, count),
        originalData: item,
      };
    });
  }, [processedData, windowWidth, windowHeight]);

  // Calculate connections between bubbles based on common schema fields
  const calculateConnections = useCallback((bubbleData: BubbleData[]) => {
    const newConnections: Connection[] = [];

    for (let i = 0; i < bubbleData.length; i++) {
      for (let j = i + 1; j < bubbleData.length; j++) {
        const bubble1 = bubbleData[i];
        const bubble2 = bubbleData[j];

        let commonFields: string[] = [];
        let strength = 0;

        // Check for common schema fields
        if (bubble1.originalData?.dataSchema && bubble2.originalData?.dataSchema) {
          const fields1 = Object.keys(bubble1.originalData.dataSchema);
          const fields2 = Object.keys(bubble2.originalData.dataSchema);
          commonFields = intersection(fields1, fields2);
          strength = commonFields.length / Math.max(fields1.length, fields2.length);
        }

        // Fallback: check for similar data tags
        if (commonFields.length === 0 && bubble1.dataTags && bubble2.dataTags) {
          const tags1 = bubble1.dataTags.split(',').map(tag => tag.trim().toLowerCase());
          const tags2 = bubble2.dataTags.split(',').map(tag => tag.trim().toLowerCase());
          const commonTags = intersection(tags1, tags2);

          if (commonTags.length > 0) {
            commonFields = commonTags;
            strength = commonTags.length / Math.max(tags1.length, tags2.length);
          }
        }

        // Create connection if any common elements found
        if (commonFields.length > 0) {
          newConnections.push({
            from: bubble1._id,
            to: bubble2._id,
            commonFields,
            strength: Math.max(strength, 0.1), // Minimum strength for visibility
          });
        }
      }
    }

    // For demo purposes: if no connections found and we have bubbles, create some sample connections
    if (newConnections.length === 0 && bubbleData.length >= 2) {
      // Connect first bubble with second bubble as a demo
      newConnections.push({
        from: bubbleData[0]._id,
        to: bubbleData[1]._id,
        commonFields: ['demo_connection'],
        strength: 0.3,
      });

      // If we have more than 2 bubbles, connect second with third
      if (bubbleData.length >= 3) {
        newConnections.push({
          from: bubbleData[1]._id,
          to: bubbleData[2]._id,
          commonFields: ['demo_connection_2'],
          strength: 0.5,
        });
      }
    }

    return newConnections;
  }, []);

  // Update bubbles when data changes
  useEffect(() => {
    const newBubbles = generateBubbles();
    setBubbles(newBubbles);
    const newConnections = calculateConnections(newBubbles);
    console.log('Generated connections:', newConnections.length, newConnections);
    setConnections(newConnections);
  }, [generateBubbles, calculateConnections]);

  // Recalculate connections when bubble positions change (with throttling)
  useEffect(() => {
    if (bubbles.length > 0) {
      const throttledConnectionUpdate = setTimeout(() => {
        const newConnections = calculateConnections(bubbles);
        setConnections(newConnections);
      }, 150); // Throttle connection updates for better performance
      
      return () => clearTimeout(throttledConnectionUpdate);
    }
  }, [bubbles, calculateConnections]);

  // Web scroll wheel handler for zooming
  useEffect(() => {
    const handleWheel = (e) => {
      if (Platform.OS === 'web' && zoomRef.current) {
        e.preventDefault();
        const zoomDelta = e.deltaY > 0 ? -0.1 : 0.1;
        zoomRef.current.zoomBy(zoomDelta);
      }
    };

    if (Platform.OS === 'web' && containerRef.current) {
      const element = containerRef.current;
      element.addEventListener('wheel', handleWheel, { passive: false });
      
      return () => {
        element.removeEventListener('wheel', handleWheel);
      };
    }
  }, []);

  // Reset zoom and position
  const resetView = () => {
    zoomRef.current?.zoomTo(1);
    zoomRef.current?.moveTo(0, 0);
  };

  // Toggle between bubble and sankey view
  const toggleViewMode = () => {
    setViewMode(prev => prev === 'bubble' ? 'sankey' : 'bubble');
  };

  // Reset bubble positions to original layout
  const resetBubblePositions = () => {
    const resetBubbles = generateBubbles();
    setBubbles(resetBubbles);
    
    // Update individual bubble components
    resetBubbles.forEach(bubble => {
      const bubbleRef = bubbleRefs.current[bubble._id];
      if (bubbleRef) {
        bubbleRef.setPosition(bubble.x, bubble.y);
      }
    });
  };

  // Handle bubble tap
  const handleBubbleTap = (bubble: BubbleData) => {
    setActiveItem(bubble.originalData);
    Alert.alert(
      bubble.dataTitle,
      `Data items: ${bubble.count}`,
      [
        { text: 'View Options', onPress: () => dataOptionsRef.current?.openModal(bubble.originalData) },
        { text: 'Actions', onPress: () => actionsRef.current?.openModal(bubble.originalData) },
        { text: 'Share', onPress: () => shareUserRef.current?.openModal(bubble.originalData) },
        { text: 'Cancel', style: 'cancel' }
      ]
    );
  };

  // Handle connection deletion
  const handleDeleteConnection = useCallback((connectionIndex: number) => {
    Alert.alert(
      'Delete Connection',
      'Are you sure you want to remove this relationship link?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: () => {
            setConnections(prevConnections => 
              prevConnections.filter((_, index) => index !== connectionIndex)
            );
          }
        }
      ]
    );
  }, []);

  // Connection Lines Component with smart visibility and interactive features
  const ConnectionLines = () => {
    // Calculate connection visibility based on zoom level and connection density
    const connectionOpacity = useMemo(() => {
      // Base opacity calculation
      let baseOpacity = 0;

      // Show connections more prominently when zoomed in
      if (zoomLevel > 1.5) {
        baseOpacity = 0.8;
      } else if (zoomLevel > 1) {
        baseOpacity = 0.5;
      } else if (zoomLevel > 0.7) {
        baseOpacity = 0.3;
      } else {
        baseOpacity = 0.1; // Very faint when zoomed out
      }

      // If there are many connections, reduce opacity to avoid clutter
      if (connections.length > 10) {
        baseOpacity *= 0.5;
      } else if (connections.length > 5) {
        baseOpacity *= 0.7;
      }

      return baseOpacity;
    }, [zoomLevel, connections.length]);

    // Filter connections to show only stronger ones when zoomed out
    const visibleConnections = useMemo(() => {
      if (zoomLevel < 1) {
        // When zoomed out, only show stronger connections
        return connections.filter(conn => conn.strength > 0.4);
      }
      return connections;
    }, [connections, zoomLevel]);

    // Android-specific: Use try-catch for SVG rendering
    try {
      return (
        <Svg
          width={VIEW_WIDTH}
          height={VIEW_HEIGHT}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            pointerEvents: 'box-none', // Allow interaction with children but not background
            zIndex: 1,
          }}
          viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`}
          {...(Platform.OS === 'android' && {
            renderToHardwareTextureAndroid: true,
            shouldRasterizeIOS: false
          })}
        >
        {visibleConnections.map((connection, index) => {
          const fromBubble = bubbles.find(b => b._id === connection.from);
          const toBubble = bubbles.find(b => b._id === connection.to);

          if (!fromBubble || !toBubble) return null;

          const lineOpacity = Math.min(connection.strength * connectionOpacity, connectionOpacity);
          const strokeWidth = Math.max(connection.strength * 4, 1);

          // Calculate bezier curve for smoother connections
          const x1 = fromBubble.x;
          const y1 = fromBubble.y;
          const x2 = toBubble.x;
          const y2 = toBubble.y;
          
          const cx1 = x1 + (x2 - x1) / 3;
          const cy1 = y1 + 50;
          const cx2 = x2 + (x1 - x2) / 3;
          const cy2 = y2 - 50;
          
          const midX = (x1 + x2) / 2;
          const midY = (y1 + y2) / 2;
          
          const path = `M ${x1} ${y1} C ${cx1} ${cy1} ${cx2} ${cy2} ${x2} ${y2}`;

          return (
            <React.Fragment key={`${connection.from}-${connection.to}`}>
              {/* Connection Path */}
              <Path
                d={path}
                fill="none"
                stroke={theme ? "#FF6B6B" : "#4ECDC4"}
                strokeWidth={strokeWidth}
                opacity={lineOpacity}
                strokeDasharray={connection.commonFields.length > 2 ? undefined : "10,5"} // undefined instead of "0" for Android compatibility
              />
              
              {/* Interactive delete button - only visible when zoomed in */}
              {zoomLevel > 1.2 && (
                <>
                  <Circle
                    cx={midX}
                    cy={midY}
                    r={8}
                    fill="rgba(255, 0, 0, 0.8)"
                    stroke="white"
                    strokeWidth={2}
                    onPress={() => handleDeleteConnection(index)}
                    pointerEvents="auto"
                  />
                  {/* Delete icon as text */}
                  <SvgText
                    x={midX}
                    y={midY + 3}
                    fontSize={12}
                    fill="white"
                    textAnchor="middle"
                    pointerEvents="none"
                  >
                    ×
                  </SvgText>
                </>
              )}
              
              {/* Connection strength indicator */}
              {zoomLevel > 1.5 && (
                <Circle
                  cx={x1 + (x2 - x1) * 0.2}
                  cy={y1 + (y2 - y1) * 0.2}
                  r={3}
                  fill={theme ? "#FF6B6B" : "#4ECDC4"}
                  opacity={connection.strength}
                />
              )}
            </React.Fragment>
          );
        })}
        </Svg>
      );
    } catch (error) {
      console.error('❌ SVG rendering error on Android:', error);
      // Return empty view on error to prevent crash
      return null;
    }
  };

  return (
    <DefaultScreen
      scrollDisabled
      screenTitle={'Data Bubble View'}
      noMargin
      backButton={true}
      style={{ paddingBottom: 50 }}
      containerStyle={{ flex: 1, paddingBottom: 50 }}
      searchBar={{
        callback: setSearchState,
        searchState: searchState,
        extraComponents: (
          <View style={{ left: 30, top: 3, flexDirection: "row", gap: 10 }}>
            <DataCounter
              extraCount={processedData.reduce((acc, item) => acc + (item?.count || 0), 0)}
              dataCount={processedData?.length || 0}
              refreshCallback={refetch}
              props={{}}
              style={{ maxWidth: 150, top: -5, left: 5 }}
            />
          </View>
        ),
        customIcons: [
          { function: toggleViewMode, icon: viewMode === 'bubble' ? <MaterialIcons name="account-tree" size={18} color={"white"} /> : <MaterialIcons name="bubble-chart" size={18} color={"white"} />, functionName: viewMode === 'bubble' ? 'sankeyView' : 'bubbleView' },
          { function: () => robotRef.current?.showHelp(), icon: <Entypo name="help" size={18} color={"white"} />, functionName: 'help' },
          { function: refetch, icon: <FontAwesome name="refresh" size={18} color={"white"} />, functionName: 'refresh' },
          { function: resetView, icon: <MaterialIcons name="center-focus-strong" size={18} color={"white"} />, functionName: 'resetView' },
          { function: resetBubblePositions, icon: <MaterialIcons name="scatter-plot" size={18} color={"white"} />, functionName: 'resetBubbles' },
          { function: () => navigation.navigate('CreateData'), icon: <MaterialIcons name="create-new-folder" size={18} color="white" />, functionName: 'createData' }
        ]
      }}
    >
      {isLoading ? (
        <View style={{ flex: 1, top: -100, alignItems: 'center', justifyContent: "center" }}>
          <Loading />
        </View>
      ) : (
        <View ref={containerRef} style={styles.bubbleContainer}>

          {/* Instructions */}
          <View style={styles.instructionsContainer}>
            <XStack gap={10} paddingHorizontal={20} paddingVertical={10} backgroundColor={theme ? '#f0f0f0' : '#333'} borderRadius={20}>
              <Text style={styles.instructionText}>
                {viewMode === 'bubble' 
                  ? '🔍 Pinch to zoom • 👆 Drag background to pan • 🎯 Tap bubbles to interact • 🫳 Drag bubbles to move • 🔗 Lines show relationships'
                  : '📊 Sankey Diagram View • 🔍 Pinch to zoom • 👆 Drag to pan • 🎯 Tap nodes to interact • Data flows by importance • Thickness shows relationship strength'
                }
              </Text>
            </XStack>
          </View>

          {/* Conditional Rendering: Bubble Chart or Sankey Diagram */}
          {viewMode === 'bubble' ? (
            /* Bubble Chart Container */
            <GestureHandlerRootView style={{ flex: 1 }}>
              <ReactNativeZoomableView
                ref={zoomRef}
                onPanResponderMove={handleZoomUpdate}
                maxZoom={5}
                  initialZoom={Platform.select({ default: 1, web: 1.2, android: 0.8 })}
                zoomStep={Platform.OS === 'web' ? 12 : 6}
                contentHeight={VIEW_HEIGHT}
                contentWidth={VIEW_WIDTH}
                bindToBorders={true}
                minZoom={0.2}
                style={{
                  flex: 1,
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              >
                {/* Connection Lines */}
                <ConnectionLines />

                {bubbles.map((bubble, index) => (
                  <BubbleComponent 
                    key={bubble._id} 
                    ref={(ref) => {
                      if (ref) {
                        bubbleRefs.current[bubble._id] = ref;
                      }
                    }}
                    bubble={bubble} 
                    index={index}
                    zoomProps={currentZoomProps}
                    onBubbleTap={handleBubbleTap}
                    onPositionChange={handleBubblePositionChange}
                    debugMode={true} // Set to true for debugging
                  />
                ))}

                {/* Empty state */}
                {bubbles.length === 0 && (
                    <View style={[
                      styles.emptyState,
                      Platform.OS === 'android' && {
                        // Android-specific optimizations to prevent crashes
                        collapsable: false,
                        needsOffscreenAlphaCompositing: false,
                      }
                    ]}>
                    <LottieView
                      source={require('@assets/animations/88006-warning-yellow.json')}
                      autoPlay
                      loop={false}
                      style={{ width: 200, height: 200 }}
                    />
                    <Text style={styles.emptyText}>No data folders to display</Text>
                  </View>
                )}
            </ReactNativeZoomableView>
          </GestureHandlerRootView>
          ) : (
            /* Sankey Diagram View */
            <SankeyDiagram
              bubbles={bubbles}
              connections={connections}
              width={windowWidth}
              height={windowHeight - 200}
              onNodeTap={handleBubbleTap}
            />
          )}

          {/* Legend - only show for bubble view */}
          {bubbles.length > 0 && viewMode === 'bubble' && (
            <View style={styles.legend}>
              <Text style={styles.legendTitle}>Bubble Size = Data Count</Text>
              <XStack gap={20}>
                <View style={styles.legendItem}>
                  <View style={[styles.legendBubble, { backgroundColor: '#FF6B6B', width: 20, height: 20 }]} />
                  <Text style={styles.legendText}>Small (1-50)</Text>
                </View>
                <View style={styles.legendItem}>
                  <View style={[styles.legendBubble, { backgroundColor: '#4ECDC4', width: 30, height: 30 }]} />
                  <Text style={styles.legendText}>Medium (51-100)</Text>
                </View>
                <View style={styles.legendItem}>
                  <View style={[styles.legendBubble, { backgroundColor: '#45B7D1', width: 40, height: 40 }]} />
                  <Text style={styles.legendText}>Large (100+)</Text>
                </View>
              </XStack>
            </View>
          )}
        </View>
      )}

      {/* Robot Helper */}
      <View style={{ position: "absolute", bottom: 0 }}>
        <SwipeableRobot ref={robotRef} videoId='tncYN17ZnFQ' textHelp={textHelp} />
      </View>
    </DefaultScreen>
  );
};

const styles = StyleSheet.create({
  bubbleContainer: {
    flex: 1,
    position: 'relative',
  },
  connectionContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    pointerEvents: 'none',
  },
  instructionsContainer: {
    position: 'absolute',
    top: 10,
    left: 0,
    right: 0,
    zIndex: 100,
    alignItems: 'center',
  },
  instructionText: {
    fontSize: 12,
    textAlign: 'center',
    opacity: 0.8,
  },
  chartContainer: {
    flex: 1,
    overflow: 'hidden',
  },
  bubbleChart: {
    flex: 1,
    position: 'relative',
  },
  emptyState: {
    position: 'absolute',
    top: '30%',
    left: 0,
    right: 0,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 40,
  },
  emptyText: {
    fontSize: 16,
    textAlign: 'center',
    marginTop: 20,
    opacity: 0.7,
  },
  legend: {
    position: 'absolute',
    bottom: 20,
    right: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    borderRadius: 10,
    padding: 15,
    elevation: 5,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
  },
  legendTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    marginBottom: 10,
    textAlign: 'center',
  },
  legendItem: {
    alignItems: 'center',
    gap: 5,
  },
  legendBubble: {
    borderRadius: 20,
  },
  legendText: {
    fontSize: 10,
    textAlign: 'center',
  },
});

export default DataBubbleScreen;
